import { CompilerClientError } from "./errors"
import { parseCreateSessionResponse, parseTemplateResponse } from "./guards"
import type {
  CompilerTemplateFile,
  CompilerTemplateResponse,
  CreateSessionRequest,
  CreateSessionResponse,
} from "./types"

interface FetchJsonOptions {
  method?: "GET" | "POST"
  body?: unknown
  timeoutMs?: number
  retries?: number
}

export class CompilerApiClient {
  /**
   * apiBase is now the local Next.js BFF prefix, e.g. "" (empty for same-origin).
   * All requests go through /api/compiler/* routes which proxy to the real backend.
   */
  constructor(private readonly apiBase: string = "") {}

  async getTemplate(templateId: string): Promise<CompilerTemplateResponse> {
    const endpoint = `/api/compiler/templates/${encodeURIComponent(templateId)}`
    console.log("[SESSION] template fetch start", { endpoint, templateId })
    const json = await this.fetchJson(endpoint, {
      method: "GET",
      retries: 1,
    })

    const parsed = parseTemplateResponse(json)
    if (!parsed) {
      console.log("[SESSION] template fetch response", {
        endpoint,
        templateId,
        valid: false,
        shape: json && typeof json === "object" ? Object.keys(json as object) : typeof json,
      })
      throw new CompilerClientError("Template payload shape is invalid", {
        code: "INVALID_TEMPLATE_PAYLOAD",
      })
    }

    console.log("[SESSION] template fetch response", {
      endpoint,
      templateId,
      valid: true,
      responseShape: {
        id: parsed.id,
        main_file: parsed.main_file,
        fileCount: parsed.files.length,
        paths: parsed.files.map((f) => f.path),
      },
    })
    return parsed
  }

  async createSession(body: CreateSessionRequest): Promise<CreateSessionResponse> {
    const safeFiles: CompilerTemplateFile[] = body.initial_files.map((file) => ({
      path: file.path,
      content: file.content,
      is_asset: file.is_asset,
    }))

    const endpoint = "/api/compiler/sessions"
    console.log("[SESSION] session create start", {
      endpoint,
      template_id: body.template_id,
      auto_compile: body.auto_compile,
      initialFileCount: safeFiles.length,
    })
    const json = await this.fetchJson(endpoint, {
      method: "POST",
      body: {
        template_id: body.template_id,
        auto_compile: body.auto_compile,
        initial_files: safeFiles,
      },
      retries: 1,
    })

    const parsed = parseCreateSessionResponse(json)
    if (!parsed) {
      console.log("[SESSION] session create response", {
        endpoint,
        valid: false,
        shape: json && typeof json === "object" ? Object.keys(json as object) : typeof json,
      })
      throw new CompilerClientError("Session payload shape is invalid", {
        code: "INVALID_SESSION_PAYLOAD",
      })
    }

    console.log("[SESSION] session create response", {
      endpoint,
      session_id: parsed.session_id,
      valid: true,
      responseShape: {
        session_id: parsed.session_id,
        ws_url: parsed.ws_url,
        auto_compile: parsed.auto_compile,
        created_at: parsed.created_at,
        pdf_url: parsed.pdf_url,
        crdt: parsed.crdt
          ? {
              room: parsed.crdt.room,
              ws_url: parsed.crdt.ws_url,
              reconstruct_url: parsed.crdt.reconstruct_url,
              history_url: parsed.crdt.history_url,
            }
          : undefined,
      },
    })
    return parsed
  }

  private async fetchJson(path: string, options: FetchJsonOptions): Promise<unknown> {
    const retries = options.retries ?? 0
    let lastError: unknown = null

    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 15000)
        try {
          const response = await fetch(`${this.apiBase}${path}`, {
            method: options.method ?? "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: options.body ? JSON.stringify(options.body) : undefined,
            signal: controller.signal,
          })

          const payload = await response
            .json()
            .catch(() => ({ message: "Malformed JSON response" }))

          if (!response.ok) {
            throw new CompilerClientError(payload?.message || "Compiler API request failed", {
              code: "API_REQUEST_FAILED",
              status: response.status,
              details: typeof payload?.message === "string" ? payload.message : undefined,
            })
          }

          return payload
        } finally {
          clearTimeout(timeout)
        }
      } catch (error) {
        lastError = error
        const isLastAttempt = attempt === retries
        if (isLastAttempt) break
      }
    }

    if (lastError instanceof CompilerClientError) {
      throw lastError
    }

    throw new CompilerClientError("Compiler API request failed", {
      code: "NETWORK_OR_TIMEOUT",
      cause: lastError,
    })
  }
}
